/**
 * Admission is the trust signal (docs/plans/2026-10-hermes-adoption.md, item 1): what the scanner found in an
 * entry or a submission, and what a node found when it started a tool server, in words beside Approve and on
 * the entry; the Verify dialog that asks one node to look again, or checks a web service there and then (no node needed).
 */

import type { CatalogueEvaluationOrder, CatalogueScan, CatalogueVerification } from '@aioe/shared';
import { Modal } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { whenWords } from './skillEvidenceWords.ts';
import { useOnlineNodes } from './SkillEvidence.tsx';
import { Button, ChoiceSelect, ErrorNote, StatusMark } from './ui.tsx';

/** The scan's findings, when there are any; a clean scan says nothing (a badge on every row is noise). */
export function ScanFindings({ scan }: { scan?: CatalogueScan }) {
  const [all, setAll] = useState(false);
  if (!scan || scan.level === 'clean' || !scan.findings.length) return null;
  const shown = all ? scan.findings : scan.findings.slice(0, 3);
  const more = scan.findings.length - shown.length;
  return (
    <div className="grid gap-1 text-sm">
      <StatusMark state={scan.level === 'dangerous' ? 'error' : 'medium'} label={scan.level === 'dangerous' ? 'Cannot be approved as it is' : `${scan.findings.length === 1 ? 'One thing' : `${scan.findings.length} things`} to look at`} />
      <ul className="m-0 grid list-none gap-0.5 p-0 text-xs text-muted">
        {shown.map((f, i) => (
          <li key={i}>
            {f.words} <span className="mono">({f.where}{f.line ? `, line ${f.line}` : ''})</span>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <button type="button" className="justify-self-start text-xs text-muted underline-offset-2 hover:underline" onClick={() => setAll(true)}>
          {more === 1 ? 'One more' : `${more} more`}
        </button>
      )}
    </div>
  );
}

/** What a node found when it started the tool server, then an outstanding or failed check. Nothing when there is neither. */
export function ServerVerification({ verified, verifying, declared, web = false }: { verified?: CatalogueVerification; verifying?: CatalogueEvaluationOrder; declared?: string[]; web?: boolean }) {
  // A web service is checked by the platform there and then, so a node's old order says nothing about it.
  const order = web ? undefined : verifying;
  if (!verified && !order) return declared === undefined ? null : <div className="text-xs text-muted">{web ? 'Not checked yet: Verify calls its address and lists what it offers.' : 'No node has checked what this server registers yet.'}</div>;
  const pending = order?.state === 'pending';
  const didNotRun = order && (order.state === 'failed' || order.state === 'refused' || order.state === 'expired');
  return (
    <div className="grid gap-1 text-sm">
      {verified && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <StatusMark state={verified.undeclared.length ? 'error' : verified.missing.length ? 'medium' : verified.signIn ? 'idle' : 'connected'} label={verified.undeclared.length ? 'Registers undeclared tools' : verified.missing.length ? 'A declared tool is missing' : verified.signIn ? 'Reached; each person signs in' : 'Verified'} />
            {verified.stale && <StatusMark state="disabled" label="Stale" />}
          </div>
          <div>{verified.sentence}</div>
          <div className="text-xs text-muted">
            {whenWords(verified.at)}
            {verified.stale ? (web ? '. The server’s address has changed since; Verify checks it again.' : '. The server’s definition has changed since; it is being checked again when a node is free.') : ''}
          </div>
        </>
      )}
      {pending && (
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted">
          <StatusMark state="working" label="Pending" />
          <span>
            {verified ? 'Asked again of' : 'Asked of'} {order.deviceName} {whenWords(order.issuedAt)}; it starts the server with no secrets, lists its tools and answers when it next reports in.
          </span>
        </div>
      )}
      {didNotRun && (
        <div className="text-xs text-muted">
          The last check on {order.deviceName} did not run{order.message ? `: ${order.message.replace(/\.$/, '')}` : ''}.
        </div>
      )}
    </div>
  );
}

/** Whether a tool server's definition is a web service, which the platform checks itself. */
export const isWebService = (content: unknown) => !!content && typeof content === 'object' && (content as { transport?: unknown }).transport === 'http';

/** Ask one online node to start a tool server and list what it registers, or, for a web service, check it now: a published entry or a pending submission. */
export function VerifyDialog({ target, web, onClose, onIssued }: { target: { kind: 'entry' | 'submission'; id: string; name: string }; web: boolean; onClose: () => void; onIssued: () => void }) {
  const { loaded, online, options } = useOnlineNodes();
  const [deviceId, setDeviceId] = useState<string>();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();
  const chosen = deviceId && options[deviceId] ? deviceId : online[0]?.deviceId;

  async function send() {
    if (!web && !chosen) return;
    setSending(true);
    setError(undefined);
    try {
      const body = web ? {} : { deviceId: chosen };
      await (target.kind === 'entry' ? routes.verifyEntry(target.id, body) : routes.verifySubmission(target.id, body));
      onIssued();
      onClose();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="md">
          <Modal.Dialog aria-label={`Verify ${target.name}`}>
            <Modal.Header>
              <Modal.Heading>Verify {target.name}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="grid gap-3 p-1">
              {web ? (
                <p className="m-0 text-sm text-muted">Its address is called now, with no secrets, and the tools it lists are compared with what the entry declares. A server that asks each person to sign in is reached, and its tools are checked when someone first uses it. Nothing is published on the result.</p>
              ) : (
                <p className="m-0 text-sm text-muted">The node starts the server in its sandbox with no secrets, lists the tools it registers and stops it. What it registers is compared with what the entry declares; nothing is published on the result.</p>
              )}
              {web ? null : loaded && !online.length ? (
                <p className="m-0 text-sm">No node is online. A tool server is checked on a node, unattended: bring one online and try again.</p>
              ) : (
                <ChoiceSelect label="Node" value={chosen} options={options} onChange={setDeviceId} placeholder={loaded ? 'Choose a node' : 'Loading nodes…'} isDisabled={!online.length} />
              )}
              <ErrorNote error={error} />
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" variant="tertiary" onPress={onClose}>
                Cancel
              </Button>
              <Button size="sm" isPending={sending} isDisabled={!web && !chosen} onPress={() => void send()}>
                Verify
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
